import { FiType } from 'react-icons/fi';

export const presentSimpleData = {
  courseId: 'present-simple',
  courseType: 'grammar',
  title: 'Present Simple Tense',
  icon: FiType,
  overview: {
    impact: 'Foundation for everyday communication',
    focus: 'Habits, routines, facts and schedules',
    uses: 'Speaking Part 1, Writing Task 1 data descriptions',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Formation',
      content: `<p>The present simple is formed with the base verb (add <strong>-s/-es</strong> for he/she/it).</p><h4>Positive:</h4><p>I/You/We/They <strong>work</strong>. He/She/It <strong>works</strong>.</p><h4>Negative:</h4><p>I/You/We/They <strong>do not (don't) work</strong>. He/She/It <strong>does not (doesn't) work</strong>.</p><h4>Question:</h4><p><strong>Do</strong> you work? <strong>Does</strong> she work?</p>`,
      examples: [
        {
          label: 'Routine',
          text: 'I wake up at 7 every morning.'
        },
        {
          label: 'Fact',
          text: 'Water boils at 100°C.'
        },
        {
          label: 'Schedule',
          text: 'The train leaves at 9:15.'
        }
      ]
    },
    {
      title: 'Spelling Rules for Third Person',
      content: `<p>Most verbs add <strong>-s</strong>: play→plays. Verbs ending in -s, -sh, -ch, -x, -o add <strong>-es</strong>: watch→watches. Verbs ending in consonant + y change to <strong>-ies</strong>: study→studies.</p>`,
      table: {
        headers: [
          'Verb',
          'Third Person',
          'Rule'
        ],
        rows: [
          [
            'play',
            'plays',
            'add -s'
          ],
          [
            'watch',
            'watches',
            'add -es'
          ],
          [
            'study',
            'studies',
            'y→ies'
          ],
          [
            'go',
            'goes',
            'add -es'
          ],
          [
            'try',
            'tries',
            'y→ies'
          ]
        ]
      }
    },
    {
      title: 'Key Uses in IELTS',
      content: `<p>Present simple is essential across all IELTS sections:</p>`,
      points: [
        'Describing habits in Speaking Part 1: \'I usually read before bed.\'',
        'Stating facts in Writing Task 1: \'The chart shows that exports increase each year.\'',
        'General truths in essays: \'Education plays a vital role in society.\'',
        'Timetables and schedules: \'The exam starts at 9 am.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Form',
      type: 'select',
      instructions: 'Select the correct present simple form.',
      questions: [
        {
          text: 'She ___ to school every day.',
          options: [
            'go',
            'goes',
            'going'
          ],
          answer: 'goes',
          explanation: 'Third person singular takes -es: go→goes.'
        },
        {
          text: 'They ___ English at university.',
          options: [
            'studies',
            'study',
            'studys'
          ],
          answer: 'study',
          explanation: '\'They\' uses the base form: study.'
        },
        {
          text: 'The museum ___ at 10 am.',
          options: [
            'open',
            'opens',
            'opening'
          ],
          answer: 'opens',
          explanation: 'Third person singular: open→opens.'
        },
        {
          text: 'I ___ coffee in the morning.',
          options: [
            'drinks',
            'drink',
            'drinking'
          ],
          answer: 'drink',
          explanation: 'First person uses the base form.'
        },
        {
          text: 'Water ___ at 0°C.',
          options: [
            'freeze',
            'freezes',
            'freezing'
          ],
          answer: 'freezes',
          explanation: 'General facts use present simple; third person: freeze→freezes.'
        }
      ]
    },
    {
      title: 'Negative & Question Forms',
      type: 'select',
      instructions: 'Choose the correct negative or question form.',
      questions: [
        {
          text: '___ you like chocolate?',
          options: [
            'Do',
            'Does',
            'Are'
          ],
          answer: 'Do',
          explanation: '\'You\' takes \'Do\' in questions.'
        },
        {
          text: 'He ___ eat meat.',
          options: [
            'don\'t',
            'doesn\'t',
            'isn\'t'
          ],
          answer: 'doesn\'t',
          explanation: 'Third person singular negative uses \'doesn\'t\'.'
        },
        {
          text: '___ the bus stop here?',
          options: [
            'Does',
            'Do',
            'Is'
          ],
          answer: 'Does',
          explanation: '\'The bus\' is third person singular → \'Does\'.'
        }
      ]
    }
  ],
  tips: [
    'Remember: no -s for I/you/we/they',
    'Use present simple for facts, habits, and timetables',
    'Common mistake: \'He don\'t\' → correct: \'He doesn\'t\'',
    'Signal words: always, usually, often, sometimes, never, every day'
  ],
  dailyPractice: [
    'Practice present simple tense concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Present Simple: Used for habits, routines, facts, and schedules. Formed with base verb (+s/es for third person). Essential for IELTS Speaking Part 1 and Writing Task 1.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...presentSimpleData,
      title: presentSimpleData.title + ' (Foundations)',
      overview: { ...presentSimpleData.overview, duration: '15-20 minutes' },
      sections: presentSimpleData.sections.slice(0, Math.max(2, Math.ceil(presentSimpleData.sections.length / 2))),
      exercises: presentSimpleData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...presentSimpleData,
      title: presentSimpleData.title + ' (Advanced)',
      overview: { ...presentSimpleData.overview, duration: '30-35 minutes' },
      sections: presentSimpleData.sections,
      exercises: presentSimpleData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(presentSimpleData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return presentSimpleData;
}
